import { apiFetch } from "./apiClient";
import type { AuditFields } from "./audit";

/** Maps to the backend's RoleDto — the real, live Roles resource.
 * branchId/roleCode are optional (as well as nullable) so existing callers
 * that build a RoleDto-shaped object without them — e.g. orgStore's role
 * wrapper, which only manages name/description/permissions — keep
 * compiling unchanged. */
export interface RoleDto extends AuditFields {
  roleId: number;
  accountId: number;
  /** Nullable — a role can be account-wide (visible to all branches) or
   * scoped to one specific branch, same convention as other master data. */
  branchId?: number | null;
  roleCode?: string | null;
  roleName: string;
  description: string;
  isActive: boolean;
}

/** Shape accepted by both create and update (matches
 * CreateRoleDto/UpdateRoleDto). branchId/roleCode are optional here (as well
 * as nullable) so existing callers that don't deal with either — e.g.
 * orgStore's role wrapper, which only manages name/description/permissions —
 * keep compiling unchanged; omitting them is equivalent to sending null. */
export interface RolePayload {
  accountId: number;
  branchId?: number | null;
  roleCode?: string | null;
  roleName: string;
  description: string;
  isActive: boolean;
}

export const listRoles = async (): Promise<RoleDto[]> => {
  const res = await apiFetch("/roles");
  return res.json();
};

export const createRole = async (payload: RolePayload): Promise<RoleDto> => {
  const res = await apiFetch("/roles", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return res.json();
};

export const updateRole = async (
  roleId: number,
  payload: RolePayload
): Promise<void> => {
  await apiFetch(`/roles/${roleId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

export const deleteRole = async (roleId: number): Promise<void> => {
  await apiFetch(`/roles/${roleId}`, { method: "DELETE" });
};
